import { ApolloError } from '@apollo/client';
import { Box, LinearProgress } from '@mui/material';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableHeader,
  SearchForm,
  Pagination,
  LoadedContent,
  Typography,
  Button,
  IPaginationProps,
} from '@occc-app/design-system';
import { RetailerNetworkLocationFragment } from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetworkLocationGroups } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { GroupLocationsTableRow } from './retailer-network-group-locations-table-row.component';

type GroupLocationsTableProps = {
  retailerNetworkUuid: string;
  locations: RetailerNetworkLocationFragment[];
  loading: boolean;
  error?: ApolloError;
  search: string | undefined;
  onSearch: (search: string | undefined) => void;
  paginationProps: IPaginationProps;
  onAddLocation: () => void;
  onViewStoreDetails: (location: RetailerNetworkLocationFragment) => void;
  onEditStore: (location: RetailerNetworkLocationFragment) => void;
  onRemoveLocation: (locationUuid: string) => void;
};

export const GroupLocationsTable = ({
  retailerNetworkUuid,
  locations,
  loading,
  error,
  search,
  onSearch,
  paginationProps,
  onAddLocation,
  onViewStoreDetails,
  onEditStore,
  onRemoveLocation,
}: GroupLocationsTableProps) => {
  const handleSearch = ({ search: value }: { search?: string }) => {
    onSearch(value || undefined);
  };

  const actions = (
    <Box display='flex' alignItems='center'>
      <SearchForm onSubmit={handleSearch} initialValues={{ search }} />
      <Can I={UpdateRetailerNetworkLocationGroups({ uuid: retailerNetworkUuid })}>
        <Button
          variant='contained'
          sx={{ minWidth: 'fit-content', ml: 2 }}
          onClick={onAddLocation}
          data-testid='rc-group-add-location'
        >
          Add Location
        </Button>
      </Can>
    </Box>
  );

  const renderRows = (items: RetailerNetworkLocationFragment[]) => (
    <>
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '18%' }}>Site ID</TableCell>
            <TableCell sx={{ width: '14%' }}>Site Name</TableCell>
            <TableCell sx={{ width: '30%' }}>Address</TableCell>
            <TableCell sx={{ width: '14%' }}>Phone Number</TableCell>
            <TableCell sx={{ width: '10%' }}>Status</TableCell>
            <TableCell sx={{ width: '14%' }} align='right'>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((location) => (
            <GroupLocationsTableRow
              key={location.uuid}
              location={location}
              retailerNetworkUuid={retailerNetworkUuid}
              onViewStoreDetails={onViewStoreDetails}
              onEditStore={onEditStore}
              onRemoveLocation={onRemoveLocation}
            />
          ))}
        </TableBody>
      </Table>
      <Pagination {...paginationProps} />
    </>
  );

  const renderEmpty = () => {
    if (loading) return <></>;
    return (
      <Box
        py={14}
        maxWidth={500}
        margin='0 auto'
        display='flex'
        alignItems='center'
        flexDirection='column'
      >
        <Typography variant='h6' align='center' paragraph>
          No locations available
        </Typography>
        <Typography align='center' color='textSecondary' paragraph>
          {search
            ? 'No locations match your search.'
            : 'There are no locations assigned to this Location Group.'}
        </Typography>
      </Box>
    );
  };

  return (
    <TableContainer>
      <TableHeader
        title='Locations'
        actions={actions}
        sx={{ '& h2': { fontSize: '1.6rem', fontWeight: 500 } }}
      />
      {loading && <LinearProgress />}
      <Box sx={{ minHeight: 480 }}>
        <LoadedContent<RetailerNetworkLocationFragment>
          error={error}
          loading={loading}
          items={locations}
          renderData={() => renderRows(locations)}
          renderEmpty={renderEmpty}
        />
      </Box>
    </TableContainer>
  );
};
